import { TradeData, TradeSourceType, TradeStatus } from "@shared/types";
import {
  getLadderCounts,
  GridLadderModel,
  LadderLevel,
  LevelsLadderModel,
  PriceLadderModel,
} from "common/NewLook/priceLadder.ts";

const SHORT_PRICE_DIGITS = 6;
const NON_ZERO_DIGIT = /[1-9]/;
const EXIT_KINDS: Partial<Record<TradeStatus, PhoneExitKind>> = {
  [TradeStatus.STOPPED_OUT]: "stopped",
  [TradeStatus.LIQUIDATED]: "liquidated",
};
const SOURCE_KINDS: Record<TradeSourceType, PhoneSourceKind> = {
  [TradeSourceType.DCA_BOT]: "dca",
  [TradeSourceType.GRID_BOT]: "grid",
  [TradeSourceType.HEDGING_BOT]: "hedging",
  [TradeSourceType.TRADING_VIEW]: "trading_view",
  [TradeSourceType.SIGNALS_BOT]: "signals",
  [TradeSourceType.GROUP]: "signals",
  [TradeSourceType.MANUAL]: "manual",
};

export interface PhoneLegendItem {
  key:
    | "entry"
    | "next_entry"
    | "price"
    | "take_profit"
    | "stop"
    | "low"
    | "high";
  count?: string;
  price?: number;
  distance?: number;
}

export type PhoneSourceKind =
  | "dca"
  | "grid"
  | "hedging"
  | "trading_view"
  | "signals"
  | "manual";

export interface PhoneTradeSource {
  kind?: PhoneSourceKind;
  name?: string;
}

export type PhoneDetailKey = "entry" | "closed";

export interface PhoneTradeDetail {
  key: PhoneDetailKey;
  value: string;
  count?: string;
}

export interface PhoneTradeDetails {
  figures: PhoneTradeDetail[];
  source: PhoneTradeSource;
  account?: string;
  status?: string;
  note?: string;
  opened?: string;
}

export interface PhoneDetailFormatters {
  price: (price: number) => string;
  date: (timestamp: number) => string;
}

export type PhoneExitKind = "exit" | "stopped" | "liquidated";

interface LadderCount {
  filled: number;
  total: number;
}

type DraftDetail = Omit<PhoneTradeDetail, "value"> & { value?: string };

export const getPhoneLadderLegend = (
  trade: TradeData,
  model: PriceLadderModel
): PhoneLegendItem[] => {
  if (model.type === "split") return [];
  if (model.type === "grid") return getGridLegend(model);
  const counts = model.isComplete ? getLadderCounts(model) : undefined;
  const current = toPrice(model.currentPrice);
  const stop = getStopPrice(trade, model);
  const takeProfit = getTakeProfitPrice(trade, model);
  return [
    { key: "stop", price: stop, distance: getDistance(stop, current) },
    getEntryItem(trade, model, counts?.entries),
    { key: "price", price: current },
    {
      key: "take_profit",
      count: formatCount(counts?.takeProfits),
      price: takeProfit,
      distance: getDistance(takeProfit, current),
    },
  ];
};

export const shortPrice = (
  price: number,
  formatPrice: (price: number) => string
) => {
  const shortened = Number(price.toPrecision(SHORT_PRICE_DIGITS));
  const formatted = formatPrice(shortened);
  return NON_ZERO_DIGIT.test(formatted)
    ? formatted
    : shortened.toLocaleString(undefined, {
        maximumSignificantDigits: SHORT_PRICE_DIGITS,
      });
};

const getDistance = (target?: number, current?: number) =>
  target === undefined || current === undefined
    ? undefined
    : ((target - current) / current) * 100;

export const getPhoneLadderEnds = (
  legend: PhoneLegendItem[],
  model: PriceLadderModel
) => {
  if (legend.length < 2) return [];
  const ends = [legend[0], legend[legend.length - 1]];
  return model.type === "levels" && model.isShort ? ends.reverse() : ends;
};

export const isWaitingForEntry = (model: PriceLadderModel) => {
  if (model.type !== "levels" || model.closePrice !== undefined) return false;
  if (model.isComplete) return !getLadderCounts(model).entries.filled;
  return !(model.progress?.entry ?? 0);
};

export const isRoutineClose = (trade: TradeData) =>
  trade.status_value === TradeStatus.FULFILLED;

export const getExitKind = (trade: TradeData): PhoneExitKind =>
  (trade.status_value !== undefined && EXIT_KINDS[trade.status_value]) ||
  "exit";

export const getTradeSource = (trade: TradeData): PhoneTradeSource => {
  const kind = SOURCE_KINDS[trade.source_type];
  return { kind, name: kind === "manual" ? undefined : trade.group };
};

export const getPhoneTradeDetails = (
  trade: TradeData,
  model: PriceLadderModel,
  isOpen: boolean,
  formatters: PhoneDetailFormatters
): PhoneTradeDetails => {
  return {
    figures: keepFilled(
      isOpen
        ? getOpenFigures(trade, model, formatters)
        : [getCloseTime(trade, formatters)]
    ),
    source: getTradeSource(trade),
    account: trade.account || undefined,
    status: isOpen ? undefined : getNotedStatus(trade),
    note: (!isOpen && trade.close_reason) || undefined,
    opened: formatTimestamp(
      trade.open_timestamp || trade.timestamp,
      formatters
    ),
  };
};

const getGridLegend = (model: GridLadderModel): PhoneLegendItem[] => [
  { key: "low", price: toPrice(model.min) },
  { key: "price", price: toPrice(model.currentPrice) },
  { key: "high", price: toPrice(model.max) },
];

const getEntryItem = (
  trade: TradeData,
  model: LevelsLadderModel,
  entries?: LadderCount
): PhoneLegendItem => {
  const hasEntry = (model.progress?.entry ?? 0) > 0 || !!entries?.filled;
  const price = hasEntry
    ? toPrice(model.averageEntry) ?? toPrice(trade.entry_price)
    : toPrice(trade.next_entry?.price) ?? nextPending(model.levels, "entry");
  return {
    key: hasEntry ? "entry" : "next_entry",
    count: formatCount(entries),
    price,
  };
};

const getStopPrice = (trade: TradeData, model: LevelsLadderModel) =>
  toPrice(trade.next_stop?.price) ??
  model.levels.find((level) => level.kind === "stop")?.price;

const getTakeProfitPrice = (trade: TradeData, model: LevelsLadderModel) =>
  toPrice(trade.next_take_profit?.price) ??
  nextPending(model.levels, "take_profit");

const getOpenFigures = (
  trade: TradeData,
  model: PriceLadderModel,
  formatters: PhoneDetailFormatters
): DraftDetail[] => {
  const entry =
    getPhoneLadderLegend(trade, model).find(isEntryItem) ??
    getAverageEntryItem(trade);
  return entry?.key === "entry" ? [toPriceDetail(entry, formatters)] : [];
};

const getAverageEntryItem = (trade: TradeData): PhoneLegendItem | undefined => {
  const price = toPrice(trade.entry_price);
  return price === undefined ? undefined : { key: "entry", price };
};

const toPriceDetail = (
  { count, price }: PhoneLegendItem,
  formatters: PhoneDetailFormatters
): DraftDetail => ({
  key: "entry",
  count,
  value: price === undefined ? undefined : formatters.price(price),
});

const isEntryItem = (item: PhoneLegendItem) =>
  item.key === "entry" || item.key === "next_entry";

const getCloseTime = (
  trade: TradeData,
  formatters: PhoneDetailFormatters
): DraftDetail => ({
  key: "closed",
  value: formatTimestamp(trade.close_timestamp, formatters),
});

const getNotedStatus = (trade: TradeData) =>
  trade.trade_status && !isRoutineClose(trade) && getExitKind(trade) === "exit"
    ? trade.trade_status
    : undefined;

const keepFilled = (details: DraftDetail[]) =>
  details.filter((detail): detail is PhoneTradeDetail => !!detail.value);

const formatTimestamp = (
  timestamp: number | undefined,
  formatters: PhoneDetailFormatters
) => (timestamp ? formatters.date(timestamp) : undefined);

const formatCount = (count?: LadderCount) =>
  count && count.total > 1 ? `${count.filled}/${count.total}` : undefined;

const isPrice = (value: unknown): value is number =>
  typeof value === "number" && isFinite(value) && value > 0;

const toPrice = (value: unknown) => {
  const price = Number(value);
  return isPrice(price) ? price : undefined;
};

const nextPending = (levels: LadderLevel[], kind: LadderLevel["kind"]) =>
  levels
    .filter((level) => level.kind === kind && level.filled < 1)
    .sort((first, second) => (first.number ?? 0) - (second.number ?? 0))[0]
    ?.price;
